import React from 'react';
import { AlertCircle, Printer, Download } from 'lucide-react';
import { ChartDataResponse } from '../types';
import { ChartComponent } from './ChartComponent';
import { MetricSelector } from './MetricSelector';
import { OverlayChart } from './OverlayChart';
import { transformToChartData, CHART_CONFIGS } from '../utils';

type PeriodDataViewProps = {
  data: ChartDataResponse;
  selectedMetrics: Set<string>;
  onMetricToggle: (dataKey: string) => void;
};

export const PeriodDataView: React.FC<PeriodDataViewProps> = ({ data, selectedMetrics, onMetricToggle }) => {
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadCSV = () => {
    // CSVヘッダー
    const headers = [
      '日付',
      '睡眠スコア',
      '睡眠効率',
      'いびき回数',
      'いびき時間',
      '覚醒ブロック数',
      '体位変更回数',
      '仰向け時間',
      '横寝時間',
      'うつ伏せ時間',
      '仰向け率',
      '横寝率',
      'うつ伏せ率',
      '深い睡眠での体位',
      'いびき時の体位',
      'レム睡眠率',
      '浅い睡眠率',
      '深い睡眠率',
      '無呼吸回数'
    ];

    // CSVデータ行を構築
    const rows = data.dates.map((date, index) => [
      date,
      data.sleep_scores[index] ?? '',
      data.sleep_efficiency[index] ?? '',
      data.snore_counts[index] ?? '',
      data.snore_times[index] ?? '',
      data.wake_block_counts[index] ?? '',
      data.posture_counts[index] ?? '',
      data.supine_times[index] ?? '',
      data.sideways_times[index] ?? '',
      data.prone_times[index] ?? '',
      data.supine_rates[index] ?? '',
      data.sideways_rates[index] ?? '',
      data.prone_rates[index] ?? '',
      data.deep_sleep_postures[index] ?? '',
      data.snoring_postures[index] ?? '',
      data.rem_time_rates[index] ?? '',
      data.light_time_rates[index] ?? '',
      data.deep_time_rates[index] ?? '',
      data.apnea_counts[index] ?? ''
    ]);

    // CSVコンテンツを作成
    const csvContent = [
      headers,
      ...rows
    ].map(row => row.map(cell => {
      // セルを文字列に変換し、カンマやダブルクォートが含まれる場合は処理
      const cellStr = String(cell);
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')).join('\n');

    // UTF-8 BOMを追加
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Blobを作成
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // ダウンロード
    link.setAttribute('href', url);
    link.setAttribute('download', `sleep_data_${data.user_id}_${data.start_date}_${data.end_date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // データが存在するが空の場合
  if (data.dates.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            データが見つかりません
          </h3>
          <p className="text-gray-600 mb-2">
            ユーザ {data.user_id} の指定期間（{data.start_date} 〜 {data.end_date}）にデータがありません。
          </p>
          <p className="text-sm text-gray-500">
            別の期間やユーザIDを試してください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ユーザ {data.user_id} の睡眠データ
            </h2>
            <p className="text-gray-600">
              期間: {data.start_date} 〜 {data.end_date}
            </p>
          </div>
          <div className="flex gap-2 no-print">
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              CSVダウンロード
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-5 h-5" />
              印刷
            </button>
          </div>
        </div>

        {/* 指標選択エリア */}
        <div className="metric-selector">
          <MetricSelector 
            selectedMetrics={selectedMetrics} 
            onMetricToggle={onMetricToggle} 
          />
        </div>

        {/* 重ね合わせグラフ */}
        <div className="overlay-chart">
          <OverlayChart 
            chartData={data} 
            selectedMetrics={selectedMetrics} 
          />
        </div>

        {/* 個別グラフ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CHART_CONFIGS.map((config) => {
            try {
              console.log('チャート作成中:', config.dataKey, data[config.dataKey]);
              const transformedData = transformToChartData(data, config.dataKey as any);
              console.log('変換済みデータ:', transformedData);
              
              return (
                <div key={config.dataKey} className="print-avoid-break">
                  <ChartComponent
                    data={transformedData}
                    config={config}
                    startDate={data.start_date}
                    endDate={data.end_date}
                  />
                </div>
              );
            } catch (error) {
              console.error('チャート作成エラー:', config.dataKey, error);
              return (
                <div key={config.dataKey} className="chart-container">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {config.title}
                  </h3>
                  <div className="flex items-center justify-center h-64 text-red-500">
                    チャートの読み込みに失敗しました
                  </div>
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};
